import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function scoreColor(score: number): string {
  // Civic-data palette: clay → amber → leaf → ravine
  if (score >= 75) return "#2d6a4f"; // ravine
  if (score >= 60) return "#52b788"; // leaf
  if (score >= 45) return "#e9c46a"; // amber
  if (score >= 30) return "#e76f51"; // clay
  return "#c1666b";
}

// Cluster names are stored PLURAL because they name a group of parks, and
// /insights plus the clustering code depend on that form. A park page's pill
// reads "Type: ...", which is a claim about one park, so the head noun is
// singularized for display only. The stored name is never changed.
//
// Deliberately a known-word rule rather than a trailing-s strip: the next
// clustering run could invent "Commons", "Campus" or "Grounds", and stripping
// the s would quietly produce nonsense. Unknown words pass through untouched.
const CLUSTER_PLURALS: Record<string, string> = {
  Parks: "Park",
  Spaces: "Space",
  Slivers: "Sliver",
};

export function singularizeClusterLabel(label: string): string {
  if (!label.trim()) return label;
  // Split off a trailing "(qualifier)" so only the head noun is considered.
  const match = label.match(/^(.*?)(\s*\([^()]*\))?$/);
  if (!match) return label;
  const head = match[1];
  const qualifier = match[2] ?? "";
  const words = head.trimEnd().split(" ");
  const last = words[words.length - 1];
  const singular = CLUSTER_PLURALS[last];
  if (!singular) return label;
  words[words.length - 1] = singular;
  return words.join(" ") + qualifier;
}

export function confidenceLabel(c: number) {
  if (c >= 0.75) return "high";
  if (c >= 0.5) return "moderate";
  return "low";
}

// Small counts read better spelled out in running prose. Anything ten or above
// stays a numeral, which is the usual editorial line and keeps real quantities
// (park counts, scores) looking like data rather than words.
const SMALL_NUMBERS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
];

export function spellSmallNumber(n: number): string {
  return Number.isInteger(n) && n >= 0 && n < SMALL_NUMBERS.length
    ? SMALL_NUMBERS[n]
    : String(n);
}
